
import { Options, Vue } from 'vue-class-component'
import BarcodeLabelList from '@/components/BarcodeLabelList.vue'
import IconRfid from '@/components/svg/IconRfid.vue'
import IconArrowBack from '@/components/svg/IconArrowBack.vue'
import RfidItemSelector from '@/views/bizBrowser/RfidItemSelector.vue'
import { CLEAR_ERROR, PAIRING, SAVE_SCANNED_BARCODES } from '@/store/actions'
import { IBarcode, IBarcodeDefinitionType, TError } from 'smartbarcode-web-core/src/utils/types/index'
import { getChildren, getEstimateBarcodeChildren, pairBarcode, getBarcode } from '@/utils/api'
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { RfidScanParsedResult } from '@/utils/bizBrowser/rfidScanResultHandler'
import errorHandler from '@/utils/errorHandler'
import { openMessage } from '@/utils/helpers'

@Options({
  components: {
    BarcodeLabelList,
    IconRfid,
    IconArrowBack,
    RfidItemSelector,
  },
  name: 'PairScanRfid',
})
export default class PairScanRfid extends Vue {
  loading = false
  dialogVisible = false
  checkScannedBarcode = false
  children = [] as IBarcode[]
  estimateChildCount = 0
  extParams = {} as Record<string, unknown>
  scannedBarcodeList: IBarcode[] = []
  showRfidScanDialog = true

  async created() {
    try {
      this.loading = true
      this.children = await getChildren(this.$route.params.barcodeId)
      const { count } = await getEstimateBarcodeChildren(this.$route.params.barcodeId)
      this.estimateChildCount = count
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.loading = false
    }
  }

  goBack() {
    this.$router.back()
  }

  async beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    if (to.name === 'scanned-barcode-list') {
      next()
      return
    }

    if (this.scannedBarcodeList.length > 0) {
      try {
        await this.$confirm(this.$t('confirm_rfid_scan_result_is_not_saved_when_leaving_page'), this.$t('warning'), {
          confirmButtonText: this.$t('ok'),
          cancelButtonText: this.$t('cancel'),
          type: 'warning',
        })
        next()
      } catch {
        next(false)
      }
    } else {
      next()
    }
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.dialogVisible = true
  }

  onCloseDialog() {
    this.dialogVisible = false
    this.checkScannedBarcode = false
  }

  barcodeTypeInfo(barcodeType: string): Record<string, IBarcodeDefinitionType> {
    return this.$store.state.project?.details?.barcodeTypes[barcodeType]
  }

  get isAllowForcePairing() {
    return this.$store.state.project?.details?.allowForcePairing || false
  }

  isBarcodeInChildrenList(barcode: IBarcode): boolean {
    return !!this.children.find((bc) => bc.id === barcode.id)
  }

  async validateBarcode(resultScanned: IBarcode, listScannedResult?: IBarcode[]) {
    const currentProjectId = this.$store.state.project.details.id
    const scannedBCProjectId = resultScanned.projectId
    if (currentProjectId !== scannedBCProjectId) {
      throw this.$t('scanned_barcode_not_same_project')
    }

    const { barcodeType, hasParent } = resultScanned

    if (this.parentBarcodeId === resultScanned?.id) throw this.$t('barcode_pairing_by_itself_error')

    if (!barcodeType) {
      throw this.$t('barcode not activated')
    }

    if (this.isBarcodeInChildrenList(resultScanned)) {
      throw this.$t('scanned_barcode_in_children_list')
    }

    if (hasParent && !this.isAllowForcePairing) {
      throw this.$t('barcode_already_paired')
    }

    const barcodeTypeItem = this.barcodeTypeInfo(barcodeType)
    if (!barcodeTypeItem?.allowToBePaired || resultScanned.isDeactivated || !resultScanned.isReadyToAddTrackData) {
      throw this.$t('barcode_can_not_be_paired')
    }

    if (
      resultScanned?.reservedParentIds?.length > 0 &&
      !resultScanned?.reservedParentIds?.includes(this.parentBarcodeId)
    ) {
      throw this.$t('errors.3046')
    }

    const listScannedId = Array.from(
      new Set([resultScanned?.id, ...(listScannedResult || []).map((result) => result?.id)])
    )

    const data = {
      parentBarcodeId: this.parentBarcodeId,
      childrenBarcodeIds: listScannedId,
      isDryRun: true,
    }
    try {
      await pairBarcode(data)
    } catch (error) {
      if (error === '3044' && this.isAllowForcePairing) {
        await this.$confirm('', this.$t('confirm_overwrite_parent'), {
          confirmButtonText: this.$t('ok'),
          confirmButtonClass: 'danger',
          cancelButtonText: this.$t('cancel'),
        })
          .then(async () => {
            const payload = {
              ...data,
              forcePairing: true,
            }
            await pairBarcode(payload)
            this.extParams = {
              ...this.extParams,
              forcePairing: true,
            }
          })
          .catch((err: unknown) => {
            throw err
          })
          .finally(() => this.$store.commit(CLEAR_ERROR))
        return
      }
      throw error
    }
  }

  get parentBarcodeId() {
    return this.$store.state.barcode?.barcode?.id || ''
  }

  async onRfidScanConfirmed(rfidList: RfidScanParsedResult[]) {
    this.loading = true
    try {
      const projectCode = this.$route?.params?.project?.toString().split('@')[0] || ''

      for (const rfidResult of rfidList) {
        try {
          const barcodeId = `projectCode=${projectCode}`
          const scannedBarcode = await getBarcode(barcodeId, rfidResult.parsedValue)

          const isAlreadyAdded = this.scannedBarcodeList.find((barcode: IBarcode) => barcode.id === scannedBarcode.id)
          if (isAlreadyAdded) {
            continue
          }

          await this.validateBarcode(scannedBarcode, this.scannedBarcodeList)
          this.scannedBarcodeList.push(scannedBarcode)
          openMessage(this.$t('barcode detected'), 'success')
        } catch (error) {
          if (error !== 'cancel') {
            errorHandler(error as TError)
          }
        }
      }
    } finally {
      this.loading = false
    }
  }

  async submit() {
    if (!this.scannedBarcodeList || this.scannedBarcodeList.length === 0) {
      return
    }
    this.$store.dispatch(SAVE_SCANNED_BARCODES, this.scannedBarcodeList)
    this.$router.push({
      name: 'scanned-barcode-list',
      params: {
        title: this.$t('pairing_read_result'),
        operation: PAIRING,
        extParams: JSON.stringify(this.extParams),
      },
    })
  }
}
